import React, { useState, useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';

interface TestInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    systemName: string;
    testerName: string;
    environment: string;
    version?: string;
    selectedCategories?: string[];
    isFullRegression: boolean;
    platforms: ('iOS' | 'Android' | 'Web')[];
  }) => void;
  categories?: string[];
  username: string;
}

const TestInfoModal: React.FC<TestInfoModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  categories = [],
  username
}) => {
  const { showToast } = useToast();
  const [systemName, setSystemName] = useState('');
  const [testerName, setTesterName] = useState(username);
  const [environment, setEnvironment] = useState('');
  const [version, setVersion] = useState('');
  const [isFullRegression, setIsFullRegression] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [showCategorySelection, setShowCategorySelection] = useState(false);
  const [platforms, setPlatforms] = useState<('iOS' | 'Android' | 'Web')[]>(['Web']);
  const [errors, setErrors] = useState<{
    systemName?: string;
    testerName?: string;
    environment?: string;
    platforms?: string;
    categories?: string;
  }>({});

  useEffect(() => {
    if (isOpen) {
      setSystemName('');
      setTesterName(username);
      setEnvironment('');
      setVersion('');
      setSelectedCategories([]);
      setIsFullRegression(true);
      setPlatforms(['Web']);
      setErrors({});
    }
  }, [isOpen, username]);

  const validateForm = () => {
    const newErrors: typeof errors = {};
    
    if (!systemName.trim()) {
      newErrors.systemName = 'Nazwa systemu jest wymagana';
    }
    if (!testerName.trim()) {
      newErrors.testerName = 'Nazwa testera jest wymagana';
    }
    if (!environment.trim()) {
      newErrors.environment = 'Środowisko jest wymagane';
    }
    if (platforms.length === 0) {
      newErrors.platforms = 'Wybierz co najmniej jedną platformę';
    }
    if (!isFullRegression && selectedCategories.length === 0) {
      newErrors.categories = 'Wybierz co najmniej jedną kategorię';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showToast('Proszę wypełnić wszystkie wymagane pola', 'warning');
      return;
    }
    
    onSubmit({
      systemName,
      testerName,
      environment,
      version: version || undefined,
      selectedCategories: !isFullRegression ? selectedCategories : undefined,
      isFullRegression,
      platforms
    });
    
    // Reset form
    setSystemName('');
    setTesterName(username);
    setEnvironment('');
    setVersion('');
    setIsFullRegression(true);
    setSelectedCategories([]);
    setShowCategorySelection(false);
    setPlatforms(['Web']);
    setErrors({});
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      }
      return [...prev, category];
    });
    // Clear category error when selecting a category
    if (errors.categories) {
      setErrors(prev => ({ ...prev, categories: undefined }));
    }
  };

  const toggleAllCategories = () => {
    setSelectedCategories(prev => 
      prev.length === categories.length ? [] : [...categories]
    );
    // Clear category error when toggling all categories
    if (errors.categories) {
      setErrors(prev => ({ ...prev, categories: undefined }));
    }
  };

  const togglePlatform = (platform: 'iOS' | 'Android' | 'Web') => {
    setPlatforms(prev => {
      if (prev.includes(platform)) {
        if (prev.length === 1) {
          showToast('Musisz wybrać co najmniej jedną platformę', 'warning');
          return prev;
        }
        return prev.filter(p => p !== platform);
      }
      return [...prev, platform];
    });
    // Clear platform error when selecting a platform
    if (errors.platforms) {
      setErrors(prev => ({ ...prev, platforms: undefined }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 dark:bg-black/60 opacity-75"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div 
          className="inline-block align-bottom bg-white dark:bg-slate-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          role="dialog" 
          aria-modal="true" 
          aria-labelledby="modal-headline"
        >
          <div className="bg-white dark:bg-slate-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="mb-6">
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white" id="modal-headline">
                Informacje o teście
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Wypełnij poniższe pola, aby rozpocząć nowy test
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="systemName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nazwa systemu <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="systemName"
                    type="text"
                    className={`w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-gray-100 dark:placeholder-gray-500 ${
                      errors.systemName ? 'border-red-500' : 'border-gray-300 dark:border-slate-600'
                    }`}
                    value={systemName}
                    onChange={(e) => {
                      setSystemName(e.target.value);
                      if (errors.systemName) {
                        setErrors(prev => ({ ...prev, systemName: undefined }));
                      }
                    }}
                    placeholder="np. Aplikacja mobilna"
                    required
                    aria-invalid={!!errors.systemName}
                    aria-describedby={errors.systemName ? 'systemName-error' : undefined}
                  />
                  {errors.systemName && (
                    <p id="systemName-error" className="mt-1 text-sm text-red-500">
                      {errors.systemName}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tester <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl">
                    <svg className="w-5 h-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="text-gray-700 font-medium">{testerName}</span>
                  </div>
                </div>

                <div>
                  <label htmlFor="environment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Środowisko <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="environment"
                    className={`w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-gray-100 ${
                      errors.environment ? 'border-red-500' : 'border-gray-300 dark:border-slate-600'
                    }`}
                    value={environment}
                    onChange={(e) => {
                      setEnvironment(e.target.value);
                      if (errors.environment) {
                        setErrors(prev => ({ ...prev, environment: undefined }));
                      }
                    }}
                    required
                    aria-invalid={!!errors.environment}
                    aria-describedby={errors.environment ? 'environment-error' : undefined}
                  >
                    <option value="">Wybierz środowisko</option>
                    <option value="DEV">DEV - Środowisko deweloperskie</option>
                    <option value="STAGE">STAGE - Środowisko testowe</option>
                    <option value="PROD">PROD - Środowisko produkcyjne</option>
                  </select>
                  {errors.environment && (
                    <p id="environment-error" className="mt-1 text-sm text-red-500">
                      {errors.environment}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="version" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Wersja (opcjonalnie)
                  </label>
                  <input
                    id="version"
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100 dark:placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    placeholder="np. 1.0.0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Platformy <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-3">
                    {(['iOS', 'Android', 'Web'] as const).map((platform) => (
                      <button
                        key={platform}
                        type="button"
                        onClick={() => togglePlatform(platform)}
                        className={`px-4 py-2 rounded-lg transition-all ${
                          platforms.includes(platform)
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 dark:bg-slate-600 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-500'
                        }`}
                        disabled={platforms.length === 1 && platforms.includes(platform)}
                        aria-pressed={platforms.includes(platform)}
                      >
                        {platform}
                      </button>
                    ))}
                  </div>
                  {errors.platforms && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.platforms}
                    </p>
                  )}
                </div>

                <div className="pt-4 border-t border-gray-200 dark:border-slate-600">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Zakres testów <span className="text-red-500">*</span>
                  </label>
                  
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        id="full-regression"
                        type="radio"
                        name="test-type"
                        className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-500 focus:ring-blue-500"
                        checked={isFullRegression}
                        onChange={() => {
                          setIsFullRegression(true);
                          setShowCategorySelection(false);
                          if (errors.categories) {
                            setErrors(prev => ({ ...prev, categories: undefined }));
                          }
                        }}
                      />
                      <label htmlFor="full-regression" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Pełne testy regresyjne (wszystkie kategorie)
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="selected-categories"
                        type="radio"
                        name="test-type"
                        className="w-4 h-4 text-blue-600 border-gray-300 dark:border-slate-500 focus:ring-blue-500"
                        checked={!isFullRegression}
                        onChange={() => {
                          setIsFullRegression(false);
                          setShowCategorySelection(true);
                        }}
                      />
                      <label htmlFor="selected-categories" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Testy wybranych kategorii
                      </label>
                    </div>
                  </div>
                </div>

                {showCategorySelection && categories.length > 0 && (
                  <div className={`mt-4 border rounded-xl p-4 ${
                    errors.categories ? 'border-red-500 bg-red-50' : 'bg-gray-50'
                  }`}>
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-sm font-medium text-gray-700">
                        Wybierz kategorie do testowania <span className="text-red-500">*</span>
                      </h4>
                      <button
                        type="button"
                        className="text-xs text-blue-600 hover:text-blue-800"
                        onClick={toggleAllCategories}
                      >
                        {selectedCategories.length === categories.length ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
                      </button>
                    </div>
                    
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {categories.map(category => (
                        <div key={category} className="flex items-center">
                          <input
                            id={`category-${category}`}
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            checked={selectedCategories.includes(category)}
                            onChange={() => toggleCategory(category)}
                          />
                          <label htmlFor={`category-${category}`} className="ml-2 block text-sm text-gray-700">
                            {category}
                          </label>
                        </div>
                      ))}
                    </div>
                    
                    {errors.categories && (
                      <p className="mt-2 text-sm text-red-500">
                        {errors.categories}
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              <div className="mt-8 flex justify-end gap-4">
                <button
                  type="button"
                  className="px-6 py-3 bg-gray-100 dark:bg-slate-600 text-gray-800 dark:text-gray-200 rounded-xl hover:bg-gray-200 dark:hover:bg-slate-500 transition-all duration-200"
                  onClick={onClose}
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={Object.keys(errors).length > 0}
                >
                  Rozpocznij testowanie
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestInfoModal; 